import { Drawer, Popover, PopoverOrigin, useMediaQuery, useTheme } from '@mui/material';
import { Dispatch, PropsWithChildren, SetStateAction } from 'react';

import styles from './DynamicSelectContainer.module.css';

type DynamicSelectContainerProps = PropsWithChildren & {
  anchorEl: HTMLElement | SVGElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | SVGElement | null>>;
  height?: string | number;
  width?: string | number;
  preventFocus?: boolean;
  anchorOptions?: {
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
  };
  /** Flag to force the Drawer render even in desktop resolutions */
  forceDrawer?: boolean;
  drawerAnchor?: 'left' | 'top' | 'right' | 'bottom';
  onTransitionExitedCallback?: () => void;
  /** When true, clicking outside the popover will not close it */
  disableClickAwayClose?: boolean;
  className?: string;
};

/**
 * Handling the container rendered depending on the screen resolution we are running.
 * In Desktop: A popover is rendered to the provided anchor.
 * In Mobile: A drawer is rendered raised up from the bottom of the screen.
 */
export default function DynamicSelectContainer({ anchorEl, setAnchorEl, ...props }: DynamicSelectContainerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Try to find the appbar and align with the bottom of it. Default to app bar height.
  const appBar = document.getElementsByTagName('header');
  const marginTop = `${String(appBar.length > 0 ? appBar[0].getBoundingClientRect().bottom : 48)}px`;

  return (
    <>
      {!isMobile && !props.forceDrawer ? (
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(_event, reason) => {
            if (props.disableClickAwayClose && reason === 'backdropClick') return;
            setAnchorEl(null);
          }}
          anchorOrigin={
            props.anchorOptions?.anchorOrigin
              ? props.anchorOptions.anchorOrigin
              : {
                  vertical: 'bottom',
                  horizontal: 'left'
                }
          }
          transformOrigin={
            props.anchorOptions?.transformOrigin
              ? props.anchorOptions.transformOrigin
              : {
                  vertical: 'top',
                  horizontal: 'left'
                }
          }
          slotProps={{
            paper: {
              className: props.className,
              sx: {
                width: props.width,
                height: props.height
              }
            }
          }}
          disableAutoFocus={props.preventFocus}
          disableEnforceFocus={props.preventFocus}
          onTransitionExited={props.onTransitionExitedCallback}>
          {props.children}
        </Popover>
      ) : (
        <Drawer
          anchor={props.drawerAnchor ? props.drawerAnchor : 'bottom'}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
          }}
          className={styles['drawer']}
          slotProps={{
            paper: {
              className: props.className,
              sx: {
                marginTop: props.drawerAnchor !== 'top' ? marginTop : undefined,
                height: props.height ?? '600px',
                width: props.width
              }
            }
          }}
          onTransitionExited={props.onTransitionExitedCallback}>
          <div className={styles['container']}>{props.children}</div>
        </Drawer>
      )}
    </>
  );
}
